import { TFunction } from '_metronic/i18n/Metronici18n'
import { IOption } from 'app/components'
import {
  ClasseRiscoDispositivo,
  DispositivoGestaoDto,
  DispositivoGestaoView,
  MotivoBaixaDispositivo,
  RegimeDispositivo,
  SituacaoDispositivo,
  TipoDispositivoGestao,
} from './_models'
import {
  opcao,
  rotuloClasseRisco,
  rotuloMotivoBaixa,
  rotuloRegime,
  rotuloSituacao,
  rotuloTipo,
} from './rotulos'
import { textoOuNulo } from './textoOuNulo'

export type ValoresDispositivo = {
  empresa?: IOption<number>
  tipo?: IOption<TipoDispositivoGestao>
  serie: string
  fabricante: string
  modelo: string
  patrimonio: string
  macAddress: string
  firmware: string
  registroAnvisa: string
  registroAnvisaValidade: string
  classeRisco?: IOption<ClasseRiscoDispositivo>
  certificacaoAnatel: string
  certificacaoInmetro: string
  lote: string
  udiDi: string
  udiPi: string
  fornecedor?: IOption<number>
  notaFiscal: string
  dtAquisicao: string
  garantiaAte: string
  regime?: IOption<RegimeDispositivo>
  periodicidadeCalibracaoMeses: string
  dtUltimaCalibracao: string
  situacao?: IOption<SituacaoDispositivo>
  motivoBaixa?: IOption<MotivoBaixaDispositivo>
  dtBaixa: string
  destinoBaixa: string
  observacoes: string
}

/**
 * Periodicidade de calibração sugerida no cadastro, em meses. Hoje 12 para todos os tipos
 * (Inmetro: anual para pressão e balança; fabricante nos demais). Fica numa função para a fase 2
 * trocar por periodicidade por modelo.
 */
export const periodicidadeSugerida = (_tipo?: TipoDispositivoGestao | null): number => 12

export const valoresIniciais = (
  t: TFunction,
  view?: DispositivoGestaoView
): ValoresDispositivo => ({
  empresa: view?.empresa ? { value: view.empresa.id, label: view.empresa.nome } : undefined,
  tipo: opcao(view?.tipo, (v) => rotuloTipo(t, v)),
  serie: view?.serie ?? '',
  fabricante: view?.fabricante ?? '',
  modelo: view?.modelo ?? '',
  patrimonio: view?.patrimonio ?? '',
  macAddress: view?.macAddress ?? '',
  firmware: view?.firmware ?? '',
  registroAnvisa: view?.registroAnvisa ?? '',
  registroAnvisaValidade: view?.registroAnvisaValidade ?? '',
  classeRisco: opcao(view?.classeRisco, (v) => rotuloClasseRisco(t, v)),
  certificacaoAnatel: view?.certificacaoAnatel ?? '',
  certificacaoInmetro: view?.certificacaoInmetro ?? '',
  lote: view?.lote ?? '',
  udiDi: view?.udiDi ?? '',
  udiPi: view?.udiPi ?? '',
  fornecedor: view?.fornecedor
    ? { value: view.fornecedor.id, label: view.fornecedor.nome }
    : undefined,
  notaFiscal: view?.notaFiscal ?? '',
  dtAquisicao: view?.dtAquisicao ?? '',
  garantiaAte: view?.garantiaAte ?? '',
  regime: opcao(view?.regime ?? 'LAUDUZ', (v) => rotuloRegime(t, v)),
  periodicidadeCalibracaoMeses: view
    ? view.periodicidadeCalibracaoMeses != null
      ? String(view.periodicidadeCalibracaoMeses)
      : ''
    : String(periodicidadeSugerida()),
  dtUltimaCalibracao: view?.dtUltimaCalibracao ?? '',
  situacao: opcao(view?.situacao ?? 'EM_ESTOQUE', (v) => rotuloSituacao(t, v)),
  motivoBaixa: opcao(view?.motivoBaixa, (v) => rotuloMotivoBaixa(t, v)),
  dtBaixa: view?.dtBaixa ?? '',
  destinoBaixa: view?.destinoBaixa ?? '',
  observacoes: view?.observacoes ?? '',
})

/**
 * Corpo do POST/PUT de gestão. Monta SEMPRE o objeto completo, porque no PUT o campo patrimonial
 * ausente ou vazio limpa o valor salvo: vazio vira `null`, MAC em maiúsculas com `:` e
 * periodicidade como número ou `null`. Os dados de baixa só vão com situação `BAIXADO`.
 */
export const paraDto = (valores: ValoresDispositivo): DispositivoGestaoDto => {
  const baixado = valores.situacao?.value === 'BAIXADO'
  const periodicidade = valores.periodicidadeCalibracaoMeses.trim()
  const mac = textoOuNulo(valores.macAddress)

  return {
    idEmpresa: valores.empresa?.value ?? null,
    tipo: valores.tipo?.value ?? null,
    serie: textoOuNulo(valores.serie),
    fabricante: textoOuNulo(valores.fabricante),
    modelo: textoOuNulo(valores.modelo),
    patrimonio: textoOuNulo(valores.patrimonio),
    macAddress: mac ? mac.toUpperCase().replace(/-/g, ':') : null,
    regime: valores.regime?.value ?? null,
    lote: textoOuNulo(valores.lote),
    udiDi: textoOuNulo(valores.udiDi),
    udiPi: textoOuNulo(valores.udiPi),
    classeRisco: valores.classeRisco?.value ?? null,
    registroAnvisa: textoOuNulo(valores.registroAnvisa),
    registroAnvisaValidade: textoOuNulo(valores.registroAnvisaValidade),
    certificacaoAnatel: textoOuNulo(valores.certificacaoAnatel),
    certificacaoInmetro: textoOuNulo(valores.certificacaoInmetro),
    firmware: textoOuNulo(valores.firmware),
    dtAquisicao: textoOuNulo(valores.dtAquisicao),
    idFornecedor: valores.fornecedor?.value ?? null,
    notaFiscal: textoOuNulo(valores.notaFiscal),
    garantiaAte: textoOuNulo(valores.garantiaAte),
    periodicidadeCalibracaoMeses: periodicidade === '' ? null : Number(periodicidade),
    dtUltimaCalibracao: textoOuNulo(valores.dtUltimaCalibracao),
    situacao: valores.situacao?.value ?? null,
    motivoBaixa: baixado ? valores.motivoBaixa?.value ?? null : null,
    dtBaixa: baixado ? textoOuNulo(valores.dtBaixa) : null,
    destinoBaixa: baixado ? textoOuNulo(valores.destinoBaixa) : null,
    observacoes: textoOuNulo(valores.observacoes),
  }
}

/** Mesmo padrão do `@Pattern` do backend. */
export const MAC_REGEX = /^([0-9A-Fa-f]{2}[:-]){5}[0-9A-Fa-f]{2}$/
